// src/enums.ts
var OrderStatus = {
  OPEN: "OPEN",
  ACCEPTED: "ACCEPTED",
  PICKED_UP: "PICKED_UP",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED",
  DISPUTED: "DISPUTED"
};
var OrderCategory = {
  DOCUMENT: "DOCUMENT",
  PHONE: "PHONE",
  ELECTRONICS: "ELECTRONICS",
  KEYS: "KEYS",
  SMALL_PACKAGE: "SMALL_PACKAGE",
  OTHER: "OTHER"
};
var SizeClass = {
  XS: "XS",
  S: "S",
  M: "M",
  L: "L",
  XL: "XL"
};
var DriverStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  SUSPENDED: "SUSPENDED"
};
var ReportStatus = {
  OPEN: "OPEN",
  RESOLVED: "RESOLVED"
};
var DisputeOutcome = {
  FOUND_DELIVERED: "FOUND_DELIVERED",
  LOST_DRIVER_FAULT: "LOST_DRIVER_FAULT",
  LOST_NO_FAULT: "LOST_NO_FAULT",
  SENDER_FAULT: "SENDER_FAULT"
};
var FeedbackType = {
  SUGGESTION: "SUGGESTION",
  COMPLAINT: "COMPLAINT",
  BUG: "BUG"
};
var Language = {
  UZ: "uz",
  RU: "ru"
};
var UploadPurpose = {
  ORDER_PHOTO: "order_photo",
  PICKUP_PHOTO: "pickup_photo",
  DELIVERY_PHOTO: "delivery_photo",
  DRIVER_DOC: "driver_doc"
};
var values = (obj) => Object.values(obj);

// src/constants.ts
var SIZE_COEFFICIENTS = {
  [SizeClass.XS]: 1,
  [SizeClass.S]: 1.3,
  [SizeClass.M]: 1.7,
  [SizeClass.L]: 2.3,
  [SizeClass.XL]: 3
};
var PRICE_RANGE_RATIO = 0.5;
var PRICE_MAX_FLOOR = 25e4;
var UZ_MOBILE_CODES = [
  "20",
  "33",
  "50",
  "55",
  "77",
  "87",
  "88",
  "90",
  "91",
  "93",
  "94",
  "95",
  "97",
  "98",
  "99"
];
var VERIFIED_MIN_DELIVERIES = 10;
var VERIFIED_MIN_RATING = 4.5;
var MAX_ACTIVE_TRIPS = 5;
var MAX_TRIP_SEATS = 10;
var TRIP_MAX_AHEAD_DAYS = 14;
var TRIP_OFFER_TTL_MS = 2 * 60 * 60 * 1e3;
var MAX_PRICE_RAISES = 3;
var MAX_SAVED_ITEMS = 20;
var MAX_ACTIVE_DRIVER_ORDERS = 5;
var DELIVERY_CODE_LENGTH = 4;
var MAX_DELIVERY_CODE_ATTEMPTS = 5;
var TEXT_LIMITS = {
  address: { min: 3, max: 300 },
  recipientName: { min: 2, max: 100 },
  orderDescription: { min: 0, max: 500 },
  reviewComment: { min: 0, max: 500 },
  report: { min: 5, max: 2e3 },
  feedback: { min: 5, max: 2e3 },
  savedLabel: { min: 1, max: 40 },
  disputeNote: { min: 3, max: 1e3 },
  rejectReason: { min: 3, max: 300 },
  tripNote: { min: 0, max: 200 },
  carModel: { min: 2, max: 60 },
  carColor: { min: 2, max: 30 }
};
var ORDER_PHOTOS_MIN = 1;
var ORDER_PHOTOS_MAX = 3;
var ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/webp"];
var MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
var APP_TIMEZONE = "Asia/Tashkent";
var ACTIVE_DRIVER_STATUSES = [
  OrderStatus.ACCEPTED,
  OrderStatus.PICKED_UP
];
var ORDER_TRANSITIONS = {
  [OrderStatus.OPEN]: [OrderStatus.ACCEPTED, OrderStatus.CANCELLED, OrderStatus.EXPIRED],
  [OrderStatus.ACCEPTED]: [OrderStatus.PICKED_UP, OrderStatus.OPEN, OrderStatus.CANCELLED],
  [OrderStatus.PICKED_UP]: [OrderStatus.DELIVERED, OrderStatus.DISPUTED],
  [OrderStatus.DELIVERED]: [OrderStatus.DISPUTED],
  [OrderStatus.CANCELLED]: [],
  [OrderStatus.EXPIRED]: [],
  // Faqat admin nizoni hal qilganda (resolve-dispute).
  [OrderStatus.DISPUTED]: [OrderStatus.DELIVERED, OrderStatus.CANCELLED]
};
var canTransition = (from, to) => ORDER_TRANSITIONS[from].includes(to);

// src/utils.ts
var roundTo1000 = (value) => Math.round(value / 1e3) * 1e3;
var suggestPrice = (basePrice, sizeClass) => roundTo1000(basePrice * SIZE_COEFFICIENTS[sizeClass]);
var priceBounds = (suggested) => ({
  min: roundTo1000(suggested * (1 - PRICE_RANGE_RATIO)),
  max: Math.max(PRICE_MAX_FLOOR, roundTo1000(suggested * (1 + PRICE_RANGE_RATIO)))
});
var formatPrice = (value, currency = "so'm") => {
  const grouped = Math.trunc(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${grouped} ${currency}`;
};
var normalizeUzPhone = (input) => {
  const digits = input.replace(/\D/g, "");
  if (digits.length === 9) return `+998${digits}`;
  if (digits.length === 12 && digits.startsWith("998")) return `+${digits}`;
  return null;
};
var isUzMobilePhone = (phone) => /^\+998\d{9}$/.test(phone) && UZ_MOBILE_CODES.includes(phone.slice(4, 6));
var priceHint = (r) => {
  if (r.orders < 3) return "ok";
  const expiredShare = r.expired / r.orders;
  if (expiredShare >= 0.3 || (r.avgAcceptMinutes ?? 0) > 6 * 60) return "raise";
  if (r.orders >= 5 && r.expired === 0 && r.avgAcceptMinutes !== null && r.avgAcceptMinutes < 30 && (r.priceRatio ?? 0) >= 1) {
    return "lower";
  }
  return "ok";
};
var isVerifiedDriver = (d) => d.approved && d.deliveriesAsDriver >= VERIFIED_MIN_DELIVERIES && d.average !== null && d.average >= VERIFIED_MIN_RATING;

// src/schemas/common.ts
import { z } from "zod";
var phoneSchema = z.string().refine(isUzMobilePhone, "phone_invalid");
var idSchema = z.uuid();
var latSchema = z.number().min(-90).max(90);
var lngSchema = z.number().min(-180).max(180);
var cursorQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20)
});
var apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional()
  })
});
var ErrorCode = {
  VALIDATION_ERROR: "VALIDATION_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  INIT_DATA_INVALID: "INIT_DATA_INVALID",
  USER_BLOCKED: "USER_BLOCKED",
  PHONE_REQUIRED: "PHONE_REQUIRED",
  DRIVER_NOT_APPROVED: "DRIVER_NOT_APPROVED",
  UPLOAD_INVALID: "UPLOAD_INVALID",
  ROUTE_NOT_SUPPORTED: "ROUTE_NOT_SUPPORTED",
  CITY_INVALID: "CITY_INVALID",
  PRICE_OUT_OF_RANGE: "PRICE_OUT_OF_RANGE",
  PICKUP_TIME_INVALID: "PICKUP_TIME_INVALID",
  ORDER_STATE_CHANGED: "ORDER_STATE_CHANGED",
  ORDER_NOT_AVAILABLE: "ORDER_NOT_AVAILABLE",
  TOO_MANY_ACTIVE_ORDERS: "TOO_MANY_ACTIVE_ORDERS",
  CODE_INVALID: "CODE_INVALID",
  CODE_LOCKED: "CODE_LOCKED",
  ALREADY_APPLIED: "ALREADY_APPLIED",
  ALREADY_REVIEWED: "ALREADY_REVIEWED",
  LIMIT_REACHED: "LIMIT_REACHED",
  TRIP_UNAVAILABLE: "TRIP_UNAVAILABLE",
  INTERNAL_ERROR: "INTERNAL_ERROR"
};

// src/schemas/order.ts
import { z as z2 } from "zod";
var orderStatusSchema = z2.enum(values(OrderStatus));
var orderCategorySchema = z2.enum(values(OrderCategory));
var sizeClassSchema = z2.enum(values(SizeClass));
var createOrderSchema = z2.object({
  fromCityId: idSchema,
  toCityId: idSchema,
  pickupAddress: z2.string().trim().min(TEXT_LIMITS.address.min).max(TEXT_LIMITS.address.max),
  pickupLat: latSchema,
  pickupLng: lngSchema,
  pickupFrom: z2.coerce.date(),
  pickupTo: z2.coerce.date(),
  dropoffAddress: z2.string().trim().min(TEXT_LIMITS.address.min).max(TEXT_LIMITS.address.max),
  dropoffLat: latSchema,
  dropoffLng: lngSchema,
  recipientName: z2.string().trim().min(TEXT_LIMITS.recipientName.min).max(TEXT_LIMITS.recipientName.max),
  recipientPhone: phoneSchema,
  category: orderCategorySchema,
  sizeClass: sizeClassSchema,
  description: z2.string().trim().max(TEXT_LIMITS.orderDescription.max).default(""),
  price: z2.number().int().positive(),
  photoKeys: z2.array(z2.string().min(1)).min(ORDER_PHOTOS_MIN).max(ORDER_PHOTOS_MAX),
  noProhibitedItems: z2.literal(true),
  /** Tanlangan safar — buyurtma avval shu shafyorga taklif bo'lib boradi. */
  tripId: z2.uuid().optional()
}).refine((o) => o.fromCityId !== o.toCityId, {
  path: ["toCityId"],
  message: "same_city"
}).refine((o) => o.pickupTo > o.pickupFrom, {
  path: ["pickupTo"],
  message: "pickup_window_invalid"
});
var createTripSchema = z2.object({
  fromCityId: idSchema,
  toCityId: idSchema,
  departAt: z2.coerce.date(),
  seats: z2.number().int().min(1).max(MAX_TRIP_SEATS),
  note: z2.string().trim().max(TEXT_LIMITS.tripNote.max).default("")
});
var tripsQuerySchema = z2.object({ fromCityId: idSchema, toCityId: idSchema });
var acceptManySchema = z2.object({
  ids: z2.array(z2.uuid()).min(1).max(MAX_ACTIVE_DRIVER_ORDERS)
});
var raisePriceSchema = z2.object({ price: z2.number().int().positive() });
var ORDER_SCOPES = ["active", "finished"];
var myOrdersQuerySchema = z2.object({
  status: z2.union([orderStatusSchema, z2.enum(ORDER_SCOPES)]).optional(),
  cursor: z2.string().optional(),
  limit: z2.coerce.number().int().min(1).max(50).default(20)
});
var availableOrdersQuerySchema = z2.object({
  fromCityId: idSchema.optional(),
  toCityId: idSchema.optional(),
  /** Toshkent sanasi (YYYY-MM-DD) — olish oynasi shu kun bilan kesishadi. */
  date: z2.iso.date().optional(),
  cursor: z2.string().optional(),
  limit: z2.coerce.number().int().min(1).max(50).default(20)
});
var cancelOrderSchema = z2.object({
  reason: z2.string().trim().max(300).optional()
});
var pickupOrderSchema = z2.object({
  pickupPhotoKey: z2.string().min(1)
});
var deliverOrderSchema = z2.object({
  code: z2.string().regex(new RegExp(`^\\d{${DELIVERY_CODE_LENGTH}}$`), "code_invalid"),
  /** Topshirish paytidagi rasm — majburiy (nizolarda dalil). */
  deliveryPhotoKey: z2.string().min(1)
});
var pricingSuggestQuerySchema = z2.object({
  fromCityId: idSchema,
  toCityId: idSchema,
  sizeClass: sizeClassSchema
});

// src/schemas/driver.ts
import { z as z3 } from "zod";
var driverStatusSchema = z3.enum(values(DriverStatus));
var driverApplySchema = z3.object({
  carModel: z3.string().trim().min(TEXT_LIMITS.carModel.min).max(TEXT_LIMITS.carModel.max),
  carColor: z3.string().trim().min(TEXT_LIMITS.carColor.min).max(TEXT_LIMITS.carColor.max),
  carPlate: z3.string().trim().toUpperCase().regex(/^[0-9A-Z ]{6,12}$/, "plate_invalid"),
  carPhotoKey: z3.string().min(1),
  licensePhotoKey: z3.string().min(1),
  selfiePhotoKey: z3.string().min(1)
});
var driverRoutesSchema = z3.object({
  routes: z3.array(z3.object({ fromCityId: idSchema, toCityId: idSchema })).max(50).refine((r) => r.every((x) => x.fromCityId !== x.toCityId), "same_city")
});
var rejectDriverSchema = z3.object({
  reason: z3.string().trim().min(TEXT_LIMITS.rejectReason.min).max(TEXT_LIMITS.rejectReason.max)
});

// src/schemas/misc.ts
import { z as z4 } from "zod";
var languageSchema = z4.enum(values(Language));
var telegramAuthSchema = z4.object({
  initData: z4.string().min(1)
});
var devAuthSchema = z4.object({
  telegramId: z4.coerce.bigint().positive()
});
var updateMeSchema = z4.object({
  firstName: z4.string().trim().min(1).max(64),
  lastName: z4.string().trim().max(64).nullable(),
  language: languageSchema
}).partial();
var presignSchema = z4.object({
  purpose: z4.enum(values(UploadPurpose)),
  contentType: z4.enum(ALLOWED_IMAGE_TYPES)
});
var reviewSchema = z4.object({
  rating: z4.number().int().min(1).max(5),
  comment: z4.string().trim().max(TEXT_LIMITS.reviewComment.max).optional()
});
var REPORT_REASONS = ["damaged", "lost", "late", "behavior", "fraud", "other"];
var reportSchema = z4.object({
  reason: z4.enum(REPORT_REASONS),
  text: z4.string().trim().min(TEXT_LIMITS.report.min).max(TEXT_LIMITS.report.max)
});
var labelSchema = z4.string().trim().min(TEXT_LIMITS.savedLabel.min).max(TEXT_LIMITS.savedLabel.max);
var savedAddressSchema = z4.object({
  label: labelSchema,
  cityId: idSchema,
  address: z4.string().trim().min(TEXT_LIMITS.address.min).max(TEXT_LIMITS.address.max),
  lat: latSchema,
  lng: lngSchema
});
var savedRecipientSchema = z4.object({
  label: labelSchema,
  name: z4.string().trim().min(TEXT_LIMITS.recipientName.min).max(TEXT_LIMITS.recipientName.max),
  phone: phoneSchema
});
var resolveDisputeSchema = z4.object({
  outcome: z4.enum(values(DisputeOutcome)),
  note: z4.string().trim().min(TEXT_LIMITS.disputeNote.min).max(TEXT_LIMITS.disputeNote.max)
});
var clientErrorSchema = z4.object({
  app: z4.enum(["miniapp", "web"]),
  message: z4.string().trim().min(1).max(1e3),
  url: z4.string().max(300).optional(),
  stack: z4.string().max(2e3).optional()
});
var feedbackSchema = z4.object({
  type: z4.enum(values(FeedbackType)),
  text: z4.string().trim().min(TEXT_LIMITS.feedback.min).max(TEXT_LIMITS.feedback.max)
});
var pricingRulesSchema = z4.object({
  rules: z4.array(
    z4.object({
      fromCityId: idSchema,
      toCityId: idSchema,
      basePrice: z4.number().int().positive()
    })
  ).min(1)
});
var adminOrdersQuerySchema = z4.object({
  status: z4.string().optional(),
  search: z4.string().trim().max(100).optional(),
  cursor: z4.string().optional(),
  limit: z4.coerce.number().int().min(1).max(50).default(20)
});
export {
  ACTIVE_DRIVER_STATUSES,
  ALLOWED_IMAGE_TYPES,
  APP_TIMEZONE,
  DELIVERY_CODE_LENGTH,
  DisputeOutcome,
  DriverStatus,
  ErrorCode,
  FeedbackType,
  Language,
  MAX_ACTIVE_DRIVER_ORDERS,
  MAX_ACTIVE_TRIPS,
  MAX_DELIVERY_CODE_ATTEMPTS,
  MAX_PRICE_RAISES,
  MAX_SAVED_ITEMS,
  MAX_TRIP_SEATS,
  MAX_UPLOAD_BYTES,
  ORDER_PHOTOS_MAX,
  ORDER_PHOTOS_MIN,
  ORDER_SCOPES,
  ORDER_TRANSITIONS,
  OrderCategory,
  OrderStatus,
  PRICE_MAX_FLOOR,
  PRICE_RANGE_RATIO,
  REPORT_REASONS,
  ReportStatus,
  SIZE_COEFFICIENTS,
  SizeClass,
  TEXT_LIMITS,
  TRIP_MAX_AHEAD_DAYS,
  TRIP_OFFER_TTL_MS,
  UZ_MOBILE_CODES,
  UploadPurpose,
  VERIFIED_MIN_DELIVERIES,
  VERIFIED_MIN_RATING,
  acceptManySchema,
  adminOrdersQuerySchema,
  apiErrorSchema,
  availableOrdersQuerySchema,
  canTransition,
  cancelOrderSchema,
  clientErrorSchema,
  createOrderSchema,
  createTripSchema,
  cursorQuerySchema,
  deliverOrderSchema,
  devAuthSchema,
  driverApplySchema,
  driverRoutesSchema,
  driverStatusSchema,
  feedbackSchema,
  formatPrice,
  idSchema,
  isUzMobilePhone,
  isVerifiedDriver,
  languageSchema,
  latSchema,
  lngSchema,
  myOrdersQuerySchema,
  normalizeUzPhone,
  orderCategorySchema,
  orderStatusSchema,
  phoneSchema,
  pickupOrderSchema,
  presignSchema,
  priceBounds,
  priceHint,
  pricingRulesSchema,
  pricingSuggestQuerySchema,
  raisePriceSchema,
  rejectDriverSchema,
  reportSchema,
  resolveDisputeSchema,
  reviewSchema,
  roundTo1000,
  savedAddressSchema,
  savedRecipientSchema,
  sizeClassSchema,
  suggestPrice,
  telegramAuthSchema,
  tripsQuerySchema,
  updateMeSchema,
  values
};
//# sourceMappingURL=index.js.map